/**
 * Candle-lighting customs by CBS locality code. See SPEC 4.7 and docs/RESEARCH.md §2.
 * Only customs confirmed by more than one source are listed; everything else uses the default.
 */

type Text = { he: string; en: string };

export type CandleTier =
  /** Confirmed by several independent sources. */
  | "established"
  /** A local custom that is disputed: shown by default, with a one-click switch. */
  | "local_custom"
  /** Communities differ: both times are shown. */
  | "two_times"
  | "default";

export interface CandleCustom {
  minutes: number;
  tier: CandleTier;
  alternateMinutes?: number;
  note?: Text;
}

export const DEFAULT_CANDLE_MINUTES = 20;

export const CANDLE_MINUTE_CHOICES = [18, 20, 22, 30, 40] as const;

const CUSTOMS: Record<number, CandleCustom> = {
  // ירושלים
  3000: { minutes: 40, tier: "established" },
  // חיפה
  4000: { minutes: 30, tier: "established" },
  // זכרון יעקב
  9300: { minutes: 30, tier: "established" },
  // פתח תקווה
  7900: {
    minutes: 40,
    tier: "local_custom",
    alternateMinutes: 20,
    note: {
      he: "מנהג העיר מימי מייסדיה, שבאו מירושלים. יש הסוברים שהמנהג אינו מחייב.",
      en: "The city's custom since its founders, who came from Jerusalem. Some hold it is not binding.",
    },
  },
  // צפת
  8000: {
    minutes: 30,
    tier: "local_custom",
    alternateMinutes: 20,
    note: {
      he: "מנהג רווח בצפת ובערי הצפון. יש המדליקים 20 דקות לפני השקיעה.",
      en: "A common custom in Tzfat and the north. Some light 20 minutes before sunset.",
    },
  },
  // טבריה
  6700: {
    minutes: 30,
    tier: "local_custom",
    alternateMinutes: 20,
    note: {
      he: "מנהג רווח בטבריה. יש המדליקים 20 דקות לפני השקיעה.",
      en: "A common custom in Tiberias. Some light 20 minutes before sunset.",
    },
  },
  // בית שמש
  2610: {
    minutes: 20,
    tier: "two_times",
    alternateMinutes: 40,
    note: {
      he: "המנהג שונה בין הקהילות בעיר: יש המדליקים 20 דקות ויש 40 דקות לפני השקיעה.",
      en: "Communities in the city differ: some light 20 minutes and some 40 minutes before sunset.",
    },
  },
};

export const CANDLE_CUSTOM_CODES: readonly number[] = Object.keys(CUSTOMS).map(Number);

export function candleCustomFor(code: number): CandleCustom {
  return CUSTOMS[code] ?? { minutes: DEFAULT_CANDLE_MINUTES, tier: "default" };
}

/**
 * Localities where "sunset by elevation" is offered: clearly higher than their surroundings
 * with an open western horizon (SPEC 4.6). [לאימות: רשימה]
 */
export const ELEVATION_SHKIA_ELIGIBLE: ReadonlySet<number> = new Set([
  3000, // ירושלים
  8000, // צפת
]);
